import React from 'react';
import PageInstructions from '@shared/components/PageInstructions';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { Page } from '../../layout';
import ApolloError from '@shared/components/ApolloError';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import GET_OBSERVATION_REPORT_DOCUMENT_BY_ID from '../queries/getObservationReportDocumentByIdQuery';
import { ObservationReportDocumentsMapper } from '../mappers/ObservationReportDocumentsMapper';
import GET_SELECTED_ORG_FOR_EVENT from '../../calendar/queries/getSelectedOrgForEventQuery';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import { userInfo } from '@apollo-internal/cache';
import { useManageObservationReportDocuments } from '../hooks/useManageObservationReportDocuments';
import { Button } from '@mui/material';
import {
  OBSERVATION_REPORT_FIELD_NAMES,
  OBSERVATION_REPORT_DOCUMENTS_STATUS,
  OBSERVATION_REPORT_DOCUMENTS_STATUS_MAP,
  OBSERVATION_REPORT_RESULT_MAP
} from '../constants';
import { FormProvider, useForm } from 'react-hook-form';
import FormInputWithRemainingChars from '@shared/components/form/FormInputWithRemainingChars';
import { ObservationReportValidator } from '../validators/ObservationReportValidator';
import FormCustomSelector from '@shared/components/form/FormCustomSelector';
import { joiResolver } from '@hookform/resolvers/joi';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import UPDATE_OBSERVATION_REPORT_DOCUMENT from '../queries/updateObservationReportDocumentMutation';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';

const PAGE_TITLE = 'Observation Report Review';
const DESCRIPTION =
  'Download and review the attached observation report. ' +
  'Update the require fields and either accept the observation report ' +
  'or ask for revisions from the Observer by returning the document.';
const RETURN_DIALOG_MESSAGE =
  'Are you sure you want to return the Observation Report to the Observer?';
const ACCEPT_DIALOG_MESSAGE = 'Are you sure you want to accept?';
const COMMENT_MAX_LENGTH = 1000;
const COMMENT_MIN_LENGTH = 25;

export const ObservationReportReviewPage = () => {
  const { observationReportId } = useParams();
  const currentUserInfo = useReactiveVar(userInfo);

  const parsedObservationReportId = parseInt(observationReportId);

  const breadcrumbPathItems = [{ name: PAGE_TITLE, url: '' }];
  const navigate = useNavigate();

  const {
    data: observationReportData,
    loading: observationReportDataLoading,
    error: observationReportDataError
  } = useQuery(GET_OBSERVATION_REPORT_DOCUMENT_BY_ID, {
    variables: { observationReportId: parsedObservationReportId }
  });

  const eventId = observationReportData?.observationReportById?.EventId;
  const observedUserId = observationReportData?.observationReportById?.ObservedUser?.Id;

  const {
    data: orgUnitsData,
    loading: orgUnitsLoading,
    error: orgUnitsError
  } = useQuery(GET_SELECTED_ORG_FOR_EVENT, {
    variables: { eventId, includeSelectedUnitsOnly: true },
    skip: !eventId
  });

  const [updateObservationReportDocument, { error: updateObservationReportDocumentError }] =
    useMutation(UPDATE_OBSERVATION_REPORT_DOCUMENT);

  const observationReport = orgUnitsData?.getSelectedOrgForEvent
    ? ObservationReportDocumentsMapper.toPageData(
        observationReportData?.observationReportById,
        orgUnitsData.getSelectedOrgForEvent
      )
    : null;

  const form = useForm({
    resolver: joiResolver(ObservationReportValidator.reviewSchema),
    values: { ...observationReport?.document, result: observationReport?.result },
    mode: 'onChange'
  });

  const { control, handleSubmit, setValue, trigger, getValues } = form;

  const { handleDownloadObservationReportFile } = useManageObservationReportDocuments(
    eventId,
    observedUserId
  );

  if (observationReportDataError || orgUnitsError || updateObservationReportDocumentError) {
    return <ApolloError />;
  }

  if (observationReportData && observationReportData.observationReportById === null) {
    return <Navigate to={getDataNotFoundPage(currentUserInfo.Tier)} />;
  }

  const document = observationReport?.document;

  const isFormDisabled = document?.status !== OBSERVATION_REPORT_DOCUMENTS_STATUS.PENDING_REVIEW;

  const resultOptions = Array.from(OBSERVATION_REPORT_RESULT_MAP, ([value, title]) => ({
    value,
    title
  }));

  const reviewerName =
    document?.status === OBSERVATION_REPORT_DOCUMENTS_STATUS.PENDING_REVIEW
      ? 'TBD'
      : document?.reviewerName;

  const handleConfirm = status => async data => {
    const observationReportDocument = ObservationReportDocumentsMapper.toReviewApolloInput(
      observationReport.id,
      data,
      status
    );
    await updateObservationReportDocument({
      variables: { observationReportDocument: observationReportDocument }
    });

    showSuccessToast(
      `Observation Report has been successfully ${status === OBSERVATION_REPORT_DOCUMENTS_STATUS.ACCEPTED ? 'accepted' : 'returned'}.`
    );

    navigate('/data-reports');
  };

  const openModalPreCondition = async () => {
    const isFormValid = await trigger();

    if (!isFormValid) {
      const validationResultData = ObservationReportValidator.validateReviewSchema(getValues());
      showErrorToast(validationResultData.error.message);
    }

    return isFormValid;
  };

  const updateFormStatus = value => {
    setValue(OBSERVATION_REPORT_FIELD_NAMES.STATUS, value);
  };

  return (
    <Page title={PAGE_TITLE} className='observation-report-review-page'>
      <Breadcrumb
        loading={orgUnitsLoading || observationReportDataLoading}
        pathItems={breadcrumbPathItems}
        annotationItems={[
          observationReport?.orgName,
          observationReport?.orgUnitNames,
          observationReport?.observedUserName
        ]}>
        {!isFormDisabled && (
          <div className='action-buttons'>
            <ConfirmDialogWithButton
              openModalBtnText='Return'
              openModalBtnType='secondary-button'
              handleOnConfirm={handleSubmit(
                handleConfirm(OBSERVATION_REPORT_DOCUMENTS_STATUS.RETURNED)
              )}
              message={RETURN_DIALOG_MESSAGE}
              openModalPreCondition={openModalPreCondition}
              openModalBtnHandleOnClick={() =>
                updateFormStatus(OBSERVATION_REPORT_DOCUMENTS_STATUS.RETURNED)
              }
            />
            <ConfirmDialogWithButton
              openModalBtnText='Accept'
              handleOnConfirm={handleSubmit(
                handleConfirm(OBSERVATION_REPORT_DOCUMENTS_STATUS.ACCEPTED)
              )}
              message={ACCEPT_DIALOG_MESSAGE}
              openModalPreCondition={openModalPreCondition}
              openModalBtnHandleOnClick={() =>
                updateFormStatus(OBSERVATION_REPORT_DOCUMENTS_STATUS.ACCEPTED)
              }
            />
          </div>
        )}
      </Breadcrumb>
      <PageInstructions title={PAGE_TITLE} description={DESCRIPTION} />

      <FormProvider {...form}>
        <div className='input-container'>
          <div className='label bold'>Observation Report:</div>
          <div className='observation-report-input'>
            <Button className='primary-button' onClick={handleDownloadObservationReportFile}>
              Download
            </Button>
          </div>
        </div>

        <div className='input-container'>
          <div className='label bold'>Status:</div>
          <div className='observation-report-input'>
            {OBSERVATION_REPORT_DOCUMENTS_STATUS_MAP.get(document?.status)}
          </div>
        </div>

        <div className='input-container'>
          <div className='label bold'>Reviewer:</div>
          <div className='observation-report-input'>{reviewerName}</div>
        </div>

        <div className='input-container'>
          <div className='label bold'>Result:</div>
          <div className='observation-report-selector-input'>
            <FormCustomSelector
              id='results-selector'
              showAsterisk={false}
              testId='results-selector'
              name={OBSERVATION_REPORT_FIELD_NAMES.RESULT}
              label='Result'
              placeholder='Result'
              options={resultOptions}
              disabled={isFormDisabled}
            />
          </div>
        </div>

        <div className='input-container'>
          <div className='label bold'>Comments:</div>
          <div className='observation-report-input'>
            <FormInputWithRemainingChars
              control={control}
              label='Comment'
              name={OBSERVATION_REPORT_FIELD_NAMES.COMMENT}
              maxLength={COMMENT_MAX_LENGTH}
              disableUnderline
              multiline={true}
              rows={5}
              minLength={COMMENT_MIN_LENGTH}
              disabled={isFormDisabled}
            />
          </div>
        </div>
      </FormProvider>
    </Page>
  );
};
